import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Zap,
  Shield,
  Sparkles,
  Loader2,
  Mail,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BorderBeam } from "@/components/magicui/border-beam";
import { MagicCard } from "@/components/magicui/magic-card";
import plansService, { type Plan } from "@/services/plansService";
import { toast } from "sonner";
import { useAppSelector } from "@/hooks/redux";
import stripeService from "@/services/stripeService";
import { captureFatalException } from "@/config/sentry";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { canAccessDashboard } from "@/utils/subscription-status";

const PlansPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get authentication state from Redux
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isContactSupportOpen, setIsContactSupportOpen] = React.useState(false);
  // Redirect authenticated users with valid subscription to dashboard
  useEffect(() => {
    if (isAuthenticated && canAccessDashboard(user)) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch plans from backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const backendPlans = await plansService.getPlans();
        setPlans(backendPlans);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch plans");
        toast.error("Failed to load subscription plans");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const guarantees = [
    {
      icon: Shield,
      title: t("plans.guarantees.noCommitment.title"),
      description: t("plans.guarantees.noCommitment.description"),
    },
    {
      icon: Zap,
      title: t("plans.guarantees.automaticUpdates.title"),
      description: t("plans.guarantees.automaticUpdates.description"),
    },
    {
      icon: Sparkles,
      title: t("plans.guarantees.flexibility.title"),
      description: t("plans.guarantees.flexibility.description"),
    },
  ];

  const handlePlanSelect = async (planId: string) => {
    const selectedPlanData = plans.find((p) => p.id === planId);
    if (!selectedPlanData) return;

    // Check if plan has a valid priceId
    if (!selectedPlanData.priceId) {
      toast.error(
        "Ce plan n'est pas encore configuré pour les paiements. Veuillez contacter l'administrateur.",
      );
      return;
    }

    try {
      // Create Stripe checkout session with promo code support enabled
      const response = await stripeService.createCheckoutSession({
        priceId: selectedPlanData.priceId,
        allowPromotionCodes: true, // Enable promo codes in Stripe Checkout
      });

      const { url } = response;
      if (url) {
        window.location.href = url;
      } else {
        // ✅ ERREUR FATALE : Session créée mais pas d'URL de redirection
        captureFatalException(
          new Error("Checkout session created but no URL returned"),
          {
            operation: "create_checkout_session",
            priceId: selectedPlanData.priceId,
            planName: selectedPlanData.name,
            userId: user?.id,
            userEmail: user?.email,
            requires_immediate_attention: true,
            impact: "payment_flow_blocked",
            financial_impact: true,
          },
        );
        toast.error("Impossible de rediriger vers Stripe. Veuillez réessayer.");
      }
    } catch (err) {
      // ✅ ERREUR FATALE : Échec de création de session checkout
      captureFatalException(err as Error, {
        operation: "create_checkout_session",
        priceId: selectedPlanData.priceId,
        planName: selectedPlanData.name,
        userId: user?.id,
        userEmail: user?.email,
        requires_immediate_attention: true,
        impact: "user_cannot_complete_payment",
        financial_impact: true,
      });

      console.error("Failed to redirect to Stripe Checkout:", err);
      toast.error(
        "Erreur lors de la redirection vers Stripe. Veuillez réessayer.",
      );
    }
  };

  const handleContactSupport = () => {
    const email = import.meta.env.VITE_SUPPORT_EMAIL;
    const subject = encodeURIComponent(
      `Demande de Support - ${import.meta.env.VITE_APP_NAME}`,
    );
    const currentDate = new Date().toLocaleDateString("fr-FR");
    const currentTime = new Date().toLocaleTimeString("fr-FR");

    const body = encodeURIComponent(
      `Bonjour l'équipe Support,

J'ai besoin d'assistance concernant :
[Veuillez décrire votre problème ou question ici]


TYPE DE PROBLÈME :
□ Problème de connexion / authentification
□ Problème avec un chatbot IA
□ Problème de facturation / abonnement
□ Problème technique (bugs, erreurs)
□ Question sur l'utilisation
□ Demande de fonctionnalité
□ Autre : _______________


DÉTAILS TECHNIQUES :
- Navigateur utilisé : ${
        navigator.userAgent.includes("Chrome")
          ? "Chrome"
          : navigator.userAgent.includes("Firefox")
            ? "Firefox"
            : navigator.userAgent.includes("Safari")
              ? "Safari"
              : navigator.userAgent.includes("Edge")
                ? "Edge"
                : "Autre"
      }
- Système d'exploitation : ${navigator.platform || "Non déterminé"}
- Date et heure : ${currentDate} à ${currentTime}
- URL de la page concernée : ${window.location.href}


INFORMATIONS UTILISATEUR :
- Email : ${user?.email || "Non renseigné"}
- Nom complet : ${user?.firstName || ""} ${user?.lastName || ""}
- Plan d'abonnement : ${user?.subscriptionPlan || "Gratuit"}
- Statut d'abonnement : ${user?.subscriptionStatus || "Non défini"}
- Email vérifié : ${user?.isEmailVerified ? "Oui" : "Non"}


ÉTAPES POUR REPRODUIRE LE PROBLÈME :
1. 
2. 
3. 

RÉSULTAT ATTENDU :


RÉSULTAT OBTENU :


Merci pour votre aide et votre support.

Cordialement,
${user?.firstName || ""} ${user?.lastName || ""}`,
    );

    // Create mailto link
    const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;

    // Open email client
    window.location.href = mailtoLink;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            Loading subscription plans...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">
            Error loading plans: {error}
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const getOriginalPrice = (price: number, discountPercentage: number) => {
    return price / (1 - discountPercentage / 100);
  };

  const getMonthlyOriginalPrice = (
    price: number,
    discountPercentage: number,
  ) => {
    return (
      Math.round((getOriginalPrice(price, discountPercentage) / 12) * 100) / 100
    );
  };

  return (
    <div className="w-full">
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
        {/* Hero Section */}
        <div className="text-center mb-6 sm:mb-8 lg:mb-12 space-y-3 sm:space-y-4 lg:space-y-6">
          <div className="relative">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-gray-900 via-primary to-primary/80 dark:from-white dark:via-primary dark:to-primary/80 bg-clip-text text-transparent mb-3 sm:mb-4 px-2 sm:px-0">
              {t("plans.title")}
            </h1>
            {/* Glow effect behind title - responsive */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/30 to-primary/20 blur-2xl sm:blur-3xl -z-10 mx-4 sm:mx-8 lg:mx-16" />
          </div>
          <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-gray-600 dark:text-gray-300 max-w-xl sm:max-w-2xl lg:max-w-3xl mx-auto leading-relaxed px-3 sm:px-4">
            {t("plans.subtitle")}
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid px-2 py-4 sm:py-6 sm:px-4 lg:py-8 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8 lg:mb-12 overflow-hidden">
          {plans.map((plan) => {
            return (
              <Card
                key={plan.id}
                className="relative group transition-all rounded-2xl duration-500 hover:scale-105  h-full flex flex-col hover:shadow-xl"
                // onClick={() => handlePlanSelect(plan.id)}
              >
                {/* Badge - affiché en haut centré si présent */}
                {plan.badge && (
                  <div className="absolute top-[-20px] left-1/2 -translate-x-1/2 z-20">
                    <div
                      className={`${
                        plan.badgeColor ||
                        "bg-gradient-to-r from-primary to-primary/80"
                      } text-white text-md sm:text-lg font-bold px-3 py-1 rounded-full shadow-lg`}
                    >
                      {plan.badge}
                    </div>
                  </div>
                )}

                {/* Gradient overlay */}
                <div className="absolute rounded-2xl inset-0 bg-gradient-to-br from-white/80 to-white/40 dark:from-gray-900/80 dark:to-gray-900/40 backdrop-blur-sm " />

                <CardHeader className="flex flex-col items-center  relative flex-grow z-10 text-center pb-3 sm:pb-4 px-3 sm:px-4 lg:px-6">
                  <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex justify-center items-center gap-2 dark:text-white mb-2">
                    {plan.name}{" "}
                    {plan.discountPercentage && plan.discountPercentage > 0 && (
                      <Badge
                        className="py-0 rounded-full text-lg  font-mono tabular-nums"
                        variant="destructive"
                      >
                        -{plan.discountPercentage}%
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="mb-3 sm:mb-4">
                    {/* Prix original barré si réduction */}
                    {plan.discountPercentage && plan.discountPercentage > 0 && (
                      <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1">
                        <span className="text-primary from-primary to-primary/80 dark:from-primary dark:to-primary/80  line-through ">
                          {getMonthlyOriginalPrice(
                            plan.price,
                            plan.discountPercentage,
                          )}
                          {plan.currency}
                        </span>
                        <span className="text-xs sm:text-sm lg:text-base font-light text-gray-500 dark:text-gray-400 ml-1 sm:ml-2">
                          HT {t("plans.perMonth")}
                        </span>
                      </div>
                    )}
                    {/* Prix affiché : prix mensuel équivalent (déjà avec réduction) */}
                    <div>
                      <span
                        className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${
                          plan.discountPercentage && plan.discountPercentage > 0
                            ? "bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500"
                            : "bg-gradient-to-r from-primary to-primary/80 dark:from-primary dark:to-primary/80"
                        } bg-clip-text text-transparent`}
                      >
                        {plan.billingInterval === "year"
                          ? (Number(plan.price) / 12).toFixed(2)
                          : Number(plan.price).toFixed(2)}
                        {plan.currency}
                      </span>
                      <span className="text-xs sm:text-sm lg:text-base font-light text-gray-500 dark:text-gray-400 ml-1 sm:ml-2">
                        HT {t("plans.perMonth")}
                      </span>
                    </div>
                    {/* Afficher le prix total annuel si c'est un plan annuel */}
                    {plan.billingInterval === "year" && (
                      <div className="text-xs sm:text-sm text-gray-400 dark:text-gray-500 mt-1">
                        ( {Number(plan.price).toFixed(2)}
                        {plan.currency} HT {t("plans.perYear")} )
                      </div>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm lg:text-base text-gray-600 dark:text-gray-300">
                    {plan.description}
                  </p>

                  {/* Free Trial Badge */}
                  {plan.billingInterval && (
                    <div className="mt-3 inline-flex items-center justify-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-full">
                      <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600 dark:text-green-400" />
                      <span className="text-xs sm:text-sm font-medium text-green-700 dark:text-green-300">
                        {plan.billingInterval === "year"
                          ? t("plans.trial.annual")
                          : t("plans.trial.monthly")}
                      </span>
                    </div>
                  )}
                </CardHeader>

                <CardContent className="relative z-10 px-3 sm:px-4 lg:px-6 flex-1 flex flex-col">
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-1">
                    {/* Display plan features */}
                    {plan.features.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-start space-x-2 sm:space-x-3"
                      >
                        <div className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center bg-green-500 mt-0.5">
                          <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                        </div>
                        <span className="text-xs sm:text-sm lg:text-base leading-relaxed text-gray-700 dark:text-gray-300">
                          {feature}
                        </span>
                      </li>
                    ))}

                    {/* Display only plan features - simplified for generic SaaS template */}
                  </ul>

                  <Button
                    className={`w-full group relative overflow-hidden text-xs sm:text-sm lg:text-base py-2.5 sm:py-3 lg:py-4 cursor-pointer ${
                      plan.priceId
                        ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlanSelect(plan.id);
                    }}
                    disabled={!plan.priceId}
                  >
                    <span className="relative z-10">
                      {plan.priceId ? t("plans.selectPlan") : "Non configuré"}
                    </span>
                    {/* Hover effect */}
                    {plan.priceId && (
                      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    )}
                  </Button>
                </CardContent>
                <BorderBeam
                  duration={6}
                  size={400}
                  className="from-transparent via-primary to-transparent"
                />
                <BorderBeam
                  duration={6}
                  delay={3}
                  size={400}
                  className="from-transparent via-primary/80 to-transparent"
                />
              </Card>
            );
          })}
        </div>

        {/* Guarantees Section */}
        <div className="mb-6 sm:mb-8 lg:mb-12">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-gray-900 dark:text-white mb-4 sm:mb-6 lg:mb-8 px-3 sm:px-4">
            {t("plans.guarantees.title")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {guarantees.map((guarantee, index) => (
              <div key={index} className="text-center group px-3 sm:px-4">
                <div className="relative inline-block mb-3 sm:mb-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-primary/20 to-primary/30 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                    <guarantee.icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-primary" />
                  </div>
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">
                  {guarantee.title}
                </h3>
                <p className="text-xs sm:text-sm lg:text-base text-gray-600 dark:text-gray-300 leading-relaxed max-w-xs sm:max-w-sm mx-auto">
                  {guarantee.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="w-full md:w-2/3 mx-auto px-3 sm:px-0 mt-6 sm:mt-8 lg:mt-12 mb-4 sm:mb-6">
          <MagicCard className="text-center rounded-2xl p-4 sm:p-6 lg:p-8 backdrop-blur-sm border border-primary/20 dark:border-primary/30">
            <h2 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              {t("plans.cta.title")}
            </h2>
            <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-gray-600 dark:text-gray-300 mb-3 sm:mb-4 lg:mb-6 max-w-lg sm:max-w-xl lg:max-w-2xl mx-auto px-2 sm:px-4">
              {t("plans.cta.description")}
            </p>

            <Dialog
              open={isContactSupportOpen}
              onOpenChange={setIsContactSupportOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 px-2 sm:px-4"
                >
                  {t("plans.cta.contact")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" />
                    {t("dashboard.contactSupport")}
                  </DialogTitle>
                  <DialogDescription>
                    {t("dashboard.contactSupportDescription")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex flex-col space-y-3">
                    <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">Email Support</p>
                          <p className="text-sm text-muted-foreground">
                            {import.meta.env.VITE_SUPPORT_EMAIL}
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={handleContactSupport}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      {t("dashboard.openEmailApp")}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      {t("dashboard.emailAppNote")}
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </MagicCard>
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default PlansPage;
